import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import services from 'api/services';
import * as authActions from 'global/actions/authActions';
import { useActions } from 'hooks/useActions';
import { Input, InfoText } from 'user/components';
import { passwordRegex } from 'util/appConstants';
import { passwordHasKeyword } from 'functions/stringValidation';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { agreementLabel, GDPRLabel } from 'user/containers/SignupPage/strings';
import { BenefitsList, BenefitsListItem } from './styles';
import { Header, Body, Footer, FooterButton } from 'components/Modals/modalElements';

const { AuthService } = services;

const english = 'en';

// Get fragment with modal functionality to convert existing user into a community user
export default function ModalContentConvertUser({ onCancel }) {
  const { user, authLoading } = useSelector((state) => state.auth);

  const { login, verifyAuthState } = useActions(authActions);

  const [isAgreementAgreed, setIsAgreementAgreed] = useState(false);
  const [isGDPRAgreed, setIsGDPRAgreed] = useState(false);
  const [communityName, setCommunityName] = useState(user.coderCommunityProfile?.name || user.name || '');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [validPassword, setValidPassword] = useState(false);
  const [passwordKeywordUsed, setPasswordKeywordUsed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!password || !user?.email) return;
    setValidPassword(passwordRegex.test(password));
    setPasswordKeywordUsed(passwordHasKeyword(password, user.email, user.name, communityName));
  }, [password, user, communityName]);

  const onConfirm = async () => {
    if (!communityName) return toast.error('Alias is required');
    if (!user.hasPassword && (!password || password !== passwordRepeat)) return toast.error('Password is required');
    if (!isAgreementAgreed || !isGDPRAgreed) return toast.error('Agreement is required');

    try {
      setLoading(true);
      let communityBody = { name: communityName, isAgreementAgreed, isGDPRAgreed };
      if (!user.hasPassword) {
        await AuthService.setPassword({ password, ...communityBody });
        await login(user.email, password, true);
      } else {
        await AuthService.joinCommunity(communityBody);
        await verifyAuthState();
      }
    } catch (ex) {
      toast.error(ex.message || (typeof ex === 'string' ? ex : 'Error joining the community'));
    } finally {
      setLoading(false);
    }
  };

  const onEnterConvertUser = (e) => e.key === 'Enter' && onConfirm();

  return (
    <>
      <Header>Join the community!</Header>
      <Body loading={authLoading || loading}>
        <BenefitsList>
          <BenefitsListItem>Save your progress as you solve exercises</BenefitsListItem>
          <BenefitsListItem>Earn achievements and experience points</BenefitsListItem>
          <BenefitsListItem>Compete against the community in exciting challenges</BenefitsListItem>
        </BenefitsList>
        <Input
          autoFocus
          type="text"
          value={communityName}
          placeholder="Alias"
          onKeyDown={onEnterConvertUser}
          onChange={(e) => setCommunityName(e.target.value)}
          onBlur={(e) => {
            setCommunityName(e.target.value.trim());
          }}
        />
        {!communityName && (
          <InfoText error={!communityName && (password || isAgreementAgreed || isGDPRAgreed)}>
            A user alias is required.
          </InfoText>
        )}
        {!user.hasPassword && (
          <>
            <Input
              type="password"
              autoFocus={!password}
              value={password}
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onEnterConvertUser}
            />
            <Input
              type="password"
              autoFocus={password && !passwordRepeat}
              value={passwordRepeat}
              placeholder="Repeat Password"
              onChange={(e) => setPasswordRepeat(e.target.value)}
              onKeyDown={onEnterConvertUser}
            />
            {passwordRepeat && password !== passwordRepeat && (
              <InfoText error={password !== passwordRepeat}>Passwords don't match</InfoText>
            )}
            <InfoText success={validPassword}>
              1. Password must be at least 10 characters long, contain a lowercase letter, one uppercase letter, and one
              digit.
            </InfoText>
            <InfoText error={passwordKeywordUsed} success={!passwordKeywordUsed && password.length}>
              2. Your Name and E-mail (username and domain of username@domain.com) should not be part of the password.
            </InfoText>
          </>
        )}
        <Checkbox
          checked={isAgreementAgreed}
          onChange={() => setIsAgreementAgreed(!isAgreementAgreed)}
          label={agreementLabel[english]}
        />
        <Checkbox checked={isGDPRAgreed} onChange={() => setIsGDPRAgreed(!isGDPRAgreed)} label={GDPRLabel[english]} />
        <i>Note: Your current code will remain</i>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Cancel" />
        <FooterButton
          onClick={onConfirm}
          label="Confirm"
          disabled={
            !communityName ||
            (!user.hasPassword && (!password || password !== passwordRepeat)) ||
            !isAgreementAgreed ||
            !isGDPRAgreed
          }
        />
      </Footer>
    </>
  );
}
